import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const m = (v) => `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PRESETS = [30, 60, 90];

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-lg font-bold">{value}</div>
  </div>
);

const Other = ({ ob }) => {
  if (!ob?.length) return null;
  return ob.map(o => (
    <div key={o.branch_id} className={`text-[11px] rounded border px-2 py-1 mb-1 ${o.qty30 > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`dormant-other-${o.branch_id}`}>
      <div className="font-semibold">{o.name}: {o.qty30 > 0 ? `✅ vendió ${o.qty30} pzs en 30 días` : o.qty90 > 0 ? `vendió ${o.qty90} pzs en 90 días` : 'tampoco se vende'}</div>
      <div className="text-slate-500">Stock allá: {o.stock} · 90 días: {o.qty90} pzs · Última: {o.last_sale_at ? new Date(o.last_sale_at).toLocaleDateString('es-MX', { dateStyle: 'medium' }) : 'nunca'}</div>
      {o.suggested > 0 && <div className="text-emerald-700 font-semibold">Sugerido mandar: {o.suggested} pzs</div>}
    </div>
  ));
};

const Row = ({ it, onPromo }) => (
  <tr data-testid={`dormant-row-${it.id}`}>
    <td className="px-3 py-2">
      <div className="font-medium">{it.name}{it.variant_name && <span className="text-primary"> · {it.variant_name}</span>}</div>
      <div className="text-[11px] text-slate-500">SKU: {it.sku || '—'}</div>
    </td>
    <td className="px-3 py-2 text-right tabular-nums">{it.stock}</td>
    <td className="px-3 py-2">
      {it.never_sold
        ? <span className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">Nunca vendido</span>
        : new Date(it.last_sale_at).toLocaleDateString('es-MX', { dateStyle: 'medium' })}
    </td>
    <td className={`px-3 py-2 text-right font-bold ${it.days_idle >= 90 ? 'text-rose-600' : it.days_idle >= 60 ? 'text-amber-600' : 'text-slate-800'}`}>
      {it.days_idle == null ? '—' : `${it.days_idle} días`}
    </td>
    <td className="px-3 py-2 text-right tabular-nums">{it.cost_value == null ? <span className="text-slate-400">Sin costo</span> : m(it.cost_value)}</td>
    <td className="px-3 py-2 text-right tabular-nums">{m(it.price_value)}</td>
    <td className="px-3 py-2 w-64"><Other ob={it.other_branches} /></td>
    <td className="px-3 py-2">
      <button onClick={() => onPromo(it)} className="text-xs px-2 py-1 rounded bg-rose-500 text-white font-semibold hover:bg-rose-600 whitespace-nowrap" data-testid={`dormant-promo-${it.id}`}>🏷️ Promo</button>
    </td>
  </tr>
);

export const DormantProductsDialog = ({ open, onClose, onPromo }) => {
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState('');
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!open) return;
    setD(null);
    axios.get(`${API}/products/dormant?days=${days}`, { withCredentials: true })
      .then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Error al cargar'));
  }, [open, days]);

  const applyCustom = () => {
    const n = parseInt(custom, 10);
    if (!n || n < 1) return toast.error('Escribe un número de días válido');
    setDays(n);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto" data-testid="dormant-dialog">
        <DialogHeader><DialogTitle>😴 Productos dormidos {d?.branch_name ? `· ${d.branch_name}` : ''}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">Sin venderse en:</span>
          {PRESETS.map(p => (
            <button key={p} onClick={() => setDays(p)} data-testid={`dormant-days-${p}`}
              className={`px-3 py-1 rounded-md text-xs font-semibold ${days === p ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-sky-50'}`}>{p} días</button>
          ))}
          <Input type="number" min="1" value={custom} onChange={e => setCustom(e.target.value)} placeholder="Otro" className="w-24 h-8" data-testid="dormant-days-custom" />
          <button onClick={applyCustom} className={`px-3 py-1 rounded-md text-xs font-semibold ${!PRESETS.includes(days) ? 'bg-sky-600 text-white' : 'bg-slate-200'}`} data-testid="dormant-days-apply">Ver</button>
        </div>
        {!d ? <p className="text-sm text-muted-foreground py-8 text-center">Cargando…</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Kpi testid="dormant-count" label="Productos dormidos" value={d.totals.count} />
              <Kpi testid="dormant-stock" label="Piezas paradas" value={d.totals.stock} />
              <Kpi testid="dormant-cost" label="Dinero invertido (costo)" value={m(d.totals.cost_value)} />
              <Kpi testid="dormant-price" label="Valor a Precio 1" value={m(d.totals.price_value)} />
            </div>
            {d.items.length === 0 ? <p className="text-sm text-center text-emerald-700 py-6">Ningún producto con stock lleva {d.days} días o más sin venderse 🎉</p> : (
              <table className="w-full text-sm border rounded" data-testid="dormant-table">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr><th className="px-3 py-2 text-left">Producto</th><th className="px-3 py-2 text-right">Stock</th><th className="px-3 py-2 text-left">Última venta</th>
                    <th className="px-3 py-2 text-right">Sin vender</th><th className="px-3 py-2 text-right">Valor costo</th><th className="px-3 py-2 text-right">Valor P1</th>
                    <th className="px-3 py-2 text-left">En otra sucursal</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody className="divide-y">{d.items.map(it => <Row key={it.id} it={it} onPromo={onPromo} />)}</tbody>
              </table>
            )}
            <p className="text-[11px] text-slate-500">Cuenta ventas en tienda y envíos de esta sucursal. Si nunca se ha vendido, los días se cuentan desde que se dio de alta.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
